import React, { useContext, useMemo, useState } from "react";
import MyDropdown from "../MyDropdown";
import { Button } from "../catalyst/button";
import { Field } from "../catalyst/fieldset";
import { Input } from "../catalyst/input";
import {
    Dropdown,
    DropdownButton,
    DropdownMenu,
    DropdownItem,
} from "../catalyst/dropdown";
import { ChevronDownIcon } from "@heroicons/react/16/solid";
import clsx from "clsx";
import { AppContext } from "../../AppContext";
import InfoIcon from "../../assets/info.svg";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";

const SOURCE_FILTERS = [
    { id: "all", label: "All Data Sources" },
    { id: "rc", label: "Past Cases" },
    { id: "snl", label: "Serial Lookup" },
    { id: "parsed", label: "Manual" },
    { id: "web", label: "Website" },
];

function SearchBarLeftSide({
    serNum,
    modNum,
    manName,
    refineQuery,
    handleSearchSNChange,
    handleSearchMNChange,
    setManName,
    handleSearchRefineQueryChange,
    handleSearch,
    submissionError,
}) {
    const {
        resultsViewMode,
        activeFilter,
        setActiveFilter,
        activeSearchTermFilter,
        setActiveSearchTermFilter,
        searchResultsPartsAll,
    } = useContext(AppContext);

    const [isInfoOpen, setIsInfoOpen] = useState(false);

    const normaliseSearchTerm = (term) =>
        typeof term === "string" ? term.trim().toLowerCase() : "";

    const formatSearchTermLabel = (term) => {
        const trimmed = term.trim();
        if (!trimmed) {
            return "";
        }
        if (trimmed === trimmed.toLowerCase()) {
            return trimmed
                .split(/\s+/)
                .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
                .join(" ");
        }
        return trimmed;
    };

    const searchTermOptions = useMemo(() => {
        const fromResults = Array.isArray(searchResultsPartsAll?.refineQueries)
            ? searchResultsPartsAll.refineQueries
            : [];
        const rawTerms =
            fromResults.length > 0
                ? fromResults
                : typeof refineQuery === "string"
                ? refineQuery.split(",")
                : [];

        const seen = new Set();
        const cleaned = [];

        rawTerms.forEach((term) => {
            if (typeof term !== "string") {
                return;
            }
            const trimmed = term.trim();
            if (!trimmed) {
                return;
            }
            const normalised = normaliseSearchTerm(trimmed);
            if (!seen.has(normalised)) {
                seen.add(normalised);
                cleaned.push(trimmed);
            }
        });

        return cleaned;
    }, [searchResultsPartsAll?.refineQueries, refineQuery]);

    const showSearchTermFilter = searchTermOptions.length > 0;
    const isAllSearchTermsSelected = activeSearchTermFilter === "all";
    const normalizedActiveSearchTerm = isAllSearchTermsSelected
        ? "all"
        : normaliseSearchTerm(activeSearchTermFilter);

    const handleSelectSearchTerm = (value) => {
        if (value === "all") {
            setActiveSearchTermFilter("all");
            return;
        }
        setActiveSearchTermFilter(value);
    };

    const getSearchTermButtonClasses = (isActive) =>
        clsx(
            "self-stretch h-10 px-4 rounded-lg border text-sm font-medium font-inter leading-tight text-left transition-colors",
            isActive
                ? "bg-neutral-200 border-neutral-400 text-neutral-900"
                : "bg-white border-neutral-300 text-neutral-700 hover:bg-neutral-100"
        );
    return (
        <div className="w-96 self-stretch px-6 py-8 bg-white rounded-xl shadow-[0px_4px_20.5px_0px_rgba(0,0,0,0.10)] outline outline-1 outline-neutral-200 inline-flex flex-col justify-start items-start gap-6">
            <div className="self-stretch flex flex-col justify-start items-start">
                <div className="self-stretch flex flex-col justify-start items-start">
                    <div className="self-stretch justify-center text-neutral-800 text-3xl font-bold font-sf-pro leading-9">
                        Part Search
                    </div>
                </div>
                <div className="self-stretch h-6 pt-2 pb-4 flex flex-col justify-start items-start">
                    <div className="self-stretch h-px relative opacity-25 border-t border-zinc-950/10" />
                </div>
            </div>

            <form
                onSubmit={handleSearch}
                className="self-stretch flex flex-col justify-start items-start gap-6"
            >
                <div className="self-stretch flex flex-col justify-start items-start gap-3">
                    <div className="justify-center text-neutral-800 text-lg font-bold font-sf-pro leading-7">
                        Manufacturer
                    </div>
                    <div className="self-stretch">
                        <MyDropdown manName={manName} setManName={setManName} />
                    </div>
                </div>

                <div className="self-stretch flex flex-col justify-start items-start gap-3">
                    <div className="justify-center text-neutral-800 text-lg font-bold font-sf-pro leading-7">
                        Serial Number
                    </div>
                    <Field className="self-stretch">
                        <Input
                            aria-label="Serial Number"
                            name="serial_number"
                            value={serNum}
                            onChange={handleSearchSNChange}
                            placeholder="Enter Serial Number"
                            className="self-stretch"
                        />
                    </Field>
                </div>

                <div className="self-stretch flex flex-col justify-start items-start gap-3">
                    <div className="justify-center text-neutral-800 text-lg font-bold font-sf-pro leading-7">
                        Model Number
                    </div>
                    <Field className="self-stretch">
                        <Input
                            aria-label="Model Number"
                            name="model_number"
                            value={modNum}
                            onChange={handleSearchMNChange}
                            placeholder="Enter Model"
                            className="self-stretch"
                        />
                    </Field>
                </div>

                <div className="self-stretch flex flex-col justify-start items-start gap-3">
                    <div className="inline-flex justify-start items-center gap-2">
                        <div className="justify-center text-neutral-800 text-lg font-bold font-sf-pro leading-7">
                            Search Terms
                        </div>
                        <Popover open={isInfoOpen} onOpenChange={setIsInfoOpen}>
                            <PopoverTrigger asChild>
                                <div
                                    className="h-7 relative cursor-pointer flex items-center"
                                    onMouseEnter={() => setIsInfoOpen(true)}
                                    onMouseLeave={() => setIsInfoOpen(false)}
                                >
                                    <img
                                        src={InfoIcon}
                                        alt="Info"
                                        className="h-full w-auto"
                                    />
                                </div>
                            </PopoverTrigger>
                            <PopoverContent
                                side="right"
                                align="center"
                                className="w-80 p-6 bg-white rounded-xl shadow-[0px_4px_36.599998474121094px_0px_rgba(0,0,0,0.30)] border-0"
                                onMouseEnter={() => setIsInfoOpen(true)}
                                onMouseLeave={() => setIsInfoOpen(false)}
                            >
                                <div className="inline-flex flex-col justify-start items-start gap-6">
                                    <div className="w-64 flex flex-col justify-start items-start gap-2">
                                        <div className="self-stretch justify-center text-neutral-900 text-base font-sf-pro leading-5">
                                            Multiple search terms supported
                                        </div>
                                        <div className="self-stretch justify-center text-neutral-500 text-sm font-normal font-sf-pro leading-5">
                                            You can search multiple terms at
                                            once!
                                            <br />
                                            Just separate them with commas.
                                        </div>
                                    </div>
                                    <div className="self-stretch flex flex-col justify-start items-start gap-3">
                                        <div className="w-64 justify-center text-neutral-900 text-sm font-sf-pro leading-4">
                                            Example:
                                        </div>
                                        <div className="self-stretch justify-center text-neutral-500 text-sm font-normal font-sf-pro leading-5">
                                            door assembly, elbow fitting, rocker
                                            switch
                                        </div>
                                    </div>
                                </div>
                            </PopoverContent>
                        </Popover>
                    </div>
                    <Field className="self-stretch">
                        <Input
                            aria-label="Search Terms"
                            name="search_terms"
                            value={refineQuery}
                            onChange={handleSearchRefineQueryChange}
                            placeholder="Part Description"
                            className="self-stretch"
                        />
                    </Field>
                </div>

                {showSearchTermFilter && (
                    <div className="self-stretch flex flex-col justify-start items-start gap-2">
                        <div className="text-neutral-800 text-sm font-semibold font-sf-pro leading-6">
                            Showing results from:
                        </div>
                        <div className="self-stretch flex flex-col gap-2">
                            <button
                                type="button"
                                onClick={() => handleSelectSearchTerm("all")}
                                className={getSearchTermButtonClasses(
                                    isAllSearchTermsSelected
                                )}
                            >
                                All Search Terms
                            </button>
                            {searchTermOptions.map((term) => {
                                const normalisedTerm =
                                    normaliseSearchTerm(term);
                                const isActive =
                                    !isAllSearchTermsSelected &&
                                    normalisedTerm ===
                                        normalizedActiveSearchTerm;
                                return (
                                    <button
                                        type="button"
                                        key={normalisedTerm || term}
                                        onClick={() =>
                                            handleSelectSearchTerm(term)
                                        }
                                        className={getSearchTermButtonClasses(
                                            isActive
                                        )}
                                    >
                                        {formatSearchTermLabel(term)}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {submissionError && (
                    <div className="self-stretch text-center text-red-600 text-sm">
                        {submissionError}
                    </div>
                )}

                <button
                    type="submit"
                    onClick={handleSearch}
                    className="self-stretch h-10 px-4 bg-zinc-900 rounded-lg inline-flex justify-center items-center hover:bg-zinc-800 transition"
                >
                    <div className="inline-flex flex-col justify-start items-center">
                        <div className="text-center justify-center text-white text-sm font-medium font-inter leading-tight">
                            Update Results
                        </div>
                    </div>
                </button>

                {(resultsViewMode === "consolidated" ||
                    resultsViewMode === "new") && (
                    <Dropdown>
                        <DropdownButton
                            as="div"
                            className="self-stretch h-10 px-4 bg-white rounded-lg shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-neutral-300 inline-flex justify-between items-center text-neutral-800 font-normal cursor-pointer text-base sm:text-sm"
                        >
                            <span>
                                {SOURCE_FILTERS.find(
                                    (f) => f.id === activeFilter
                                )?.label || "All Data Sources"}
                            </span>
                            <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50 text-neutral-500" />
                        </DropdownButton>
                        <DropdownMenu
                            anchor="bottom"
                            className="w-80 p-2 bg-white rounded-md shadow-[0px_4px_36.599998474121094px_0px_rgba(0,0,0,0.10)] outline outline-1 outline-offset-[-1px] outline-neutral-200"
                        >
                            {SOURCE_FILTERS.map((filter) => (
                                <DropdownItem
                                    key={filter.id}
                                    onClick={() => setActiveFilter(filter.id)}
                                    className={clsx(
                                        "h-9 px-2 rounded inline-flex items-center data-[focus]:bg-neutral-100",
                                        filter.id === activeFilter
                                            ? "bg-neutral-200"
                                            : "hover:bg-neutral-100"
                                    )}
                                >
                                    <div className="text-left text-neutral-800 font-medium text-base sm:text-sm">
                                        {filter.label}
                                    </div>
                                </DropdownItem>
                            ))}
                        </DropdownMenu>
                    </Dropdown>
                )}
            </form>
        </div>
    );
}

export default SearchBarLeftSide;
